import type { ReactNode } from "react";
import { useHashRoute } from "@/routing/useHashRoute";
import { veilHostsFor } from "@/content/veilPlacements";
import { isRouteId, type RouteId } from "@/routing/routes";
import { LivingCrossing } from "@/components/living-crossing/LivingCrossing";
import { OperatingSpine } from "@/components/content/OperatingSpine";
import "./ContentNodes.css";
import "./Earth3Hero.css";

/** Allowlisted element tree produced by scripts/extract-content.py */
export type ContentNode =
  | string
  | {
      tag: string;
      attrs?: Record<string, string>;
      children?: ContentNode[];
    };

const VOID_TAGS = new Set(["br"]);

const PASSTHROUGH_ATTRS = new Set([
  "id",
  "colspan",
  "rowspan",
  "scope",
  "lang",
  "aria-label",
  "aria-labelledby",
  "aria-describedby",
]);

function classList(attrs?: Record<string, string>): string[] {
  return (attrs?.class ?? "").split(/\s+/).filter(Boolean);
}

function reactAttrs(
  attrs: Record<string, string> | undefined,
  veilHosts?: ReadonlySet<string>,
) {
  const out: Record<string, string | number | boolean> = {};
  if (!attrs) return out;
  const isVeilHost = Boolean(attrs.id && veilHosts?.has(attrs.id));
  for (const [key, value] of Object.entries(attrs)) {
    if (key === "class") {
      out.className = isVeilHost ? `${value} veil-host` : value;
    } else if (key === "colspan") {
      out.colSpan = Number(value);
    } else if (key === "rowspan") {
      out.rowSpan = Number(value);
    } else if (key === "aria-hidden") {
      out["aria-hidden"] = value === "true";
    } else if (PASSTHROUGH_ATTRS.has(key)) {
      out[key] = value;
    }
  }
  if (isVeilHost && !out.className) out.className = "veil-host";
  return out;
}

type RenderContext = {
  navigate: (id: RouteId) => void;
  veilHosts: ReadonlySet<string>;
};

function renderChildren(children: ContentNode[] | undefined, ctx: RenderContext): ReactNode[] {
  return (children ?? []).map((child, index) => (
    <NodeRenderer key={index} node={child} ctx={ctx} />
  ));
}

/** Reject leaked implementation text that must never appear in the UI. */
function isImplementationLeak(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  return (
    /^\(function\s*\(/.test(trimmed) ||
    /^function\s*\(/.test(trimmed) ||
    /document\.(querySelector|getElementById|addEventListener)/.test(trimmed) ||
    /history\.pushState/.test(trimmed) ||
    /<\/?script\b/i.test(trimmed)
  );
}

function NodeRenderer({ node, ctx }: { node: ContentNode; ctx: RenderContext }) {
  if (typeof node === "string") {
    if (isImplementationLeak(node)) return null;
    return node;
  }

  const classes = classList(node.attrs);

  // Swap the signature instrument and spine for their accessible components
  if (classes.includes("living-model")) return <LivingCrossing />;
  if (classes.includes("spine-ribbon")) return <OperatingSpine />;

  // Route buttons keep canonical hash navigation
  const routeTarget = node.attrs?.["data-route"] ?? node.attrs?.["data-go"];
  if (node.tag === "button" && routeTarget && isRouteId(routeTarget)) {
    const target = routeTarget;
    return (
      <button
        type="button"
        {...reactAttrs(node.attrs, ctx.veilHosts)}
        onClick={() => ctx.navigate(target)}
      >
        {renderChildren(node.children, ctx)}
      </button>
    );
  }

  const scrollTarget = node.attrs?.["data-scroll-to"];
  if (node.tag === "button" && scrollTarget) {
    return (
      <button
        type="button"
        {...reactAttrs(node.attrs, ctx.veilHosts)}
        onClick={() =>
          document
            .getElementById(scrollTarget)
            ?.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      >
        {renderChildren(node.children, ctx)}
      </button>
    );
  }

  if (node.tag === "button") {
    return (
      <button type="button" {...reactAttrs(node.attrs, ctx.veilHosts)}>
        {renderChildren(node.children, ctx)}
      </button>
    );
  }

  if (node.tag === "a") {
    const href = node.attrs?.href ?? "#";
    const hash = href.startsWith("#") ? href.slice(1) : null;
    if (hash && isRouteId(hash)) {
      const target = hash;
      return (
        <a
          href={href}
          {...reactAttrs(node.attrs, ctx.veilHosts)}
          onClick={(event) => {
            event.preventDefault();
            ctx.navigate(target);
          }}
        >
          {renderChildren(node.children, ctx)}
        </a>
      );
    }
    const external = /^https?:/i.test(href);
    return (
      <a
        href={href}
        {...reactAttrs(node.attrs, ctx.veilHosts)}
        {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
      >
        {renderChildren(node.children, ctx)}
      </a>
    );
  }

  if (VOID_TAGS.has(node.tag)) {
    const Tag = node.tag as "br";
    return <Tag {...reactAttrs(node.attrs, ctx.veilHosts)} />;
  }

  const Tag = node.tag as keyof React.JSX.IntrinsicElements;

  // Wide ledgers and the scenario table scroll horizontally on narrow screens.
  // A scrollable region must be reachable by keyboard.
  const isScrollableRegion =
    classes.includes("comparison") || classes.includes("scenario-scroll");

  return (
    <Tag
      {...reactAttrs(node.attrs, ctx.veilHosts)}
      {...(isScrollableRegion ? { tabIndex: 0 } : {})}
    >
      {renderChildren(node.children, ctx)}
    </Tag>
  );
}

export function ContentNodes({ nodes, route }: { nodes: ContentNode[]; route: RouteId }) {
  const { navigate } = useHashRoute();
  const veilHosts = veilHostsFor(route);
  return <>{renderChildren(nodes, { navigate, veilHosts })}</>;
}
